import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { MenuEditor } from '../components/menu/MenuEditor';
import { MenuViewer } from '../components/menu/UserMenus';
import { useFonts } from '../components/menu/fonts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MenuEditorPage() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const fonts = useFonts();
  const [menu, setMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/menus/${menuId}`, auth)
      .then((r) => setMenu(r.data))
      .catch(() => { toast.error('Menu not found'); navigate('/admin'); });
  }, [menuId]);

  const save = useCallback(async (data) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/menus/${menuId}`, data, auth);
      setMenu((m) => ({ ...m, ...res.data }));
      setLastSavedAt(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save menu');
    } finally {
      setSaving(false);
    }
  }, [menuId]);

  const toggleStatus = async () => {
    const status = menu.status === 'published' ? 'draft' : 'published';
    try {
      const res = await axios.put(`${API}/admin/menus/${menuId}`, { status }, auth);
      setMenu((m) => ({ ...m, status: res.data.status }));
      toast.success(status === 'published' ? 'Published — members can see it now' : 'Menu unpublished');
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  if (!menu) {
    return <div className="flex min-h-screen items-center justify-center bg-[#09090b]"><Loader2 className="h-6 w-6 animate-spin text-[#A855F7]" /></div>;
  }

  return (
    <>
      <MenuEditor
        menu={menu}
        fonts={fonts}
        saving={saving}
        lastSavedAt={lastSavedAt}
        status={menu.status}
        onToggleStatus={toggleStatus}
        onSave={save}
        onBack={() => navigate('/admin', { state: { tab: 'menus' } })}
        onPreview={(data) => setPreview({ ...menu, ...data })}
      />
      <AnimatePresence>
        {preview && <MenuViewer menu={preview} customFonts={fonts.customFonts} onClose={() => setPreview(null)} />}
      </AnimatePresence>
    </>
  );
}
